import React, { useContext, useMemo } from "react";
import {
    Box,
    Checkbox,
    FormGroup,
    FormControlLabel,
    Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import ColorSelector from "../colorSelector/colorSelector";
import tinycolor from "tinycolor2";
import { HlcAreaSeriesOptions } from "../chartV2/customSeries/hlcAreaSeries/options";
import CommonChartSettingsTabs from './commonChartSettingsTabs';

type Props = {
    viewKey: string;
};

const HlcAreaSettings = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])
    const highLineColor = useMemo(() => {
        let color = '#26a69a';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.highLineColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const lowLineColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.lowLineColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const closeLineColor = useMemo(() => {
        let color = '#26a69a';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.closeLineColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const topFillColor = useMemo(() => {
        let color = '#ef5350'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.topFillColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const bottomFillColor = useMemo(() => {
        let color = '#26a69a'
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.bottomFillColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])


    const highLineVisible = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.highLineVisible ?? true
        }
        return false
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])

    const lowLineVisible = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.lowLineVisible ?? false
        }
        return false
    }, [chartConfig.series, chartConfig?.seriesGroups, primarySeriesGroupId])


    const handleColorChangeSeries = (newColor: string, type: keyof HlcAreaSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: newColor
            } as Partial<HlcAreaSeriesOptions>);
        }
    };
    const handleBooleanChange = (value: boolean, type: keyof HlcAreaSeriesOptions) => {
        if (chart) {
            chart.primarySeriesGroup?.getPrimarySeries()?.updateOptions({
                [type]: value
            } as Partial<HlcAreaSeriesOptions>);
        }
    };



    const symbolTabContent = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormGroup>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <FormControlLabel sx={{ width: 90, "& .MuiFormControlLabel-label": { fontSize: "14px" }, marginLeft: "-3px" }} control={

                        <Checkbox
                            checked={highLineVisible}
                            onChange={(e) =>
                                handleBooleanChange(e.target.checked, "highLineVisible" as any)
                            }
                        />} label="High"

                    />
                    <ColorSelector compact showOnlySwatch color={highLineColor.toHexString()} opacity={highLineColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'highLineColor');
                        }} />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <FormControlLabel sx={{ width: 90, "& .MuiFormControlLabel-label": { fontSize: "14px" }, marginLeft: "-3px" }} control={
                        <Checkbox
                            checked={lowLineVisible}
                            onChange={(e) =>
                                handleBooleanChange(e.target.checked, "lowLineVisible" as any)
                            }
                        />
                    }
                        label="Low"

                    />
                    <ColorSelector compact showOnlySwatch color={lowLineColor.toHexString()} opacity={lowLineColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'lowLineColor');
                        }} />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "103px" }}>Close</Typography>
                    <ColorSelector compact showOnlySwatch color={closeLineColor.toHexString()} opacity={closeLineColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'closeLineColor');
                        }} />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.1 }}>
                    <Typography variant="body2" sx={{ width: "103px" }}>Fill</Typography>
                    <ColorSelector compact showOnlySwatch color={topFillColor.toHexString()} opacity={topFillColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'topFillColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={bottomFillColor.toHexString()} opacity={bottomFillColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'bottomFillColor');
                        }} />
                </Box>
            </FormGroup>
        </Box>
    )

    return <CommonChartSettingsTabs viewKey={viewKey} symbolTabContent={symbolTabContent} />
}

export default HlcAreaSettings;
